import { GetOptionChainRequestV2 } from "../../proto/generated/candle-data-depot-option-chain"
import { TimeBucket_TimeSeries } from "../../proto/generated/common"
import { Broker_OrderType, TransactionType } from "../../proto/generated/order"
import { OptionType, Security, SecurityCategory_CategoryCode } from "../../proto/generated/security"
import { StrategyTemplateLegProto, StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType, StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType, StrategyTemplateProto } from "../../proto/generated/strategy"
import { OrderDataPrices, OrdersDataType, setOrderDataFunction } from "../../reducer/orders.reducer"
import store from "../../store"
import { fetchOptionChainV2 } from "../../util/options"
import { findGivenDeltaStrike, findGivenDiffStrike, findGivenPriceStrike, findGivenSdStrike, findNearestStrikeData } from "../../util/strategyBuilder"
import { futureTradeDataType, optionTradeDataType, optionsTradesDataType } from "../../pages/typings/options.typings"
import { v4 as uuid } from "uuid"
import { getCurentOrderLtpPricesFromData, getCurentOrderPricesFromData } from "../../util/tradepanel"
import { getSecurityAsync, getSecurityKey } from "../../util/security"
import commonActions from "../../actions/common.actions"
import { WidgetListItems } from "../../util/sidebar"
import { EqualityOperator } from "./StrategyTemplate.types"
import { OptionChainViewKey } from "../../assets/constants"
import { setSimulatorOrderDataFunction, SimulatorStateType } from "../../reducer/simulator.reducer"
import { TradeOrder, updateTradeOrders } from "../../reducer/strategyBuilder.reducer"

export const strategyParser = async (strategyTemplate: StrategyTemplateProto, security: Security, optionChain: optionsTradesDataType[], optionChainViewKey: OptionChainViewKey, dontSetOrdersInState?: boolean) => {
    const orders: OrdersDataType[] = optionChainViewKey===OptionChainViewKey.LIVE ? [] : (store.getState().simulatorStore as SimulatorStateType).orders.filter((o)=>o.traded)
    let orderPrices: OrderDataPrices[] = optionChainViewKey===OptionChainViewKey.LIVE ? [] : (store.getState().simulatorStore as SimulatorStateType).ordersPrices.filter((o)=>o.traded)
    const tradeOrders: TradeOrder[] = [];    
    let newOptionChain = optionChain
    let futuresData: futureTradeDataType[] = []
    const timeStamp = store.getState()?.simulatorStore?.timestamp
    if(!optionChain || optionChain.length === 0 || optionChainViewKey === OptionChainViewKey.LIVE) {
        const payload: GetOptionChainRequestV2 = {
            security: { code: security.code, exchange: security.exchange },
            expiries: [],
            noOfStrikes: -1,
            requestedFields: [],
            timeBucket: {
                timeSeries: TimeBucket_TimeSeries.DAY,
                value: 1
            },
            sendFutures: true
        }
        const {optionsData, futuresData: futuresDataFromApi} = await fetchOptionChainV2(payload, store.dispatch, newOptionChain, 'both', false)
        newOptionChain = optionsData
        futuresData = futuresDataFromApi
    }
    let monthlyOptionChain = newOptionChain.filter(o => !o.derived).filter(o => !o.expiration.weeklyExpiry)
    const legToExpiry: Record<number, number> = {}
    const weeklyLegsExpiry: (number | undefined)[] = [undefined, undefined]
    const monthlyLegsForRollOver: number[] = []
    const nextMonthlyLegsForRollOver: number[] = []
    for (let i = 0; i < (strategyTemplate.strategyTemplateLegs?.strategyTemplateLegs?.length ?? 0); i++) {
        const strategyLeg = strategyTemplate.strategyTemplateLegs?.strategyTemplateLegs[i] as StrategyTemplateLegProto
        switch (strategyLeg.expirySelection?.expirySelectionType) {
            case StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.MONTHLY:
                legToExpiry[i] = monthlyOptionChain[0].expiration.expiry?.expiry as number
                if (strategyLeg.expirySelection.rolloverFlag) monthlyLegsForRollOver.push(i)
                break
            case StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.NEXT_MONTHLY:
                legToExpiry[i] = monthlyOptionChain[1].expiration.expiry?.expiry as number
                if (strategyLeg.expirySelection.rolloverFlag) nextMonthlyLegsForRollOver.push(i)
                break
            case StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.WEEKLY:
                legToExpiry[i] = newOptionChain[0].expiration.expiry?.expiry as number
                weeklyLegsExpiry[0] = legToExpiry[i]
                break
            case StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.NEXT_WEEKLY:
                legToExpiry[i] = newOptionChain[1].expiration.expiry?.expiry as number
                weeklyLegsExpiry[1] = legToExpiry[i]
                break
            case StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.MINIMUM_DAYS_TO_MONTHLY_EXPIRY:
                let index = 0
                for(let i=0; i<monthlyOptionChain.length; i++) {
                    if((monthlyOptionChain[i].expiration.expiry?.numberOfDaysToExpiry??0)>=(strategyLeg.expirySelection.minimumDaysToExpiry??0)) {
                        index = i
                        break
                    }
                }
                legToExpiry[i] = monthlyOptionChain[index].expiration.expiry?.expiry as number
                break
        }
    }

    if(monthlyLegsForRollOver.length) {
        let expiry = monthlyOptionChain[0].expiration.expiry?.expiry as number
        if(
            (weeklyLegsExpiry[0] && weeklyLegsExpiry[0]===expiry) ||
            (weeklyLegsExpiry[1] && weeklyLegsExpiry[1]===expiry)
        ) {
            expiry = monthlyOptionChain[1].expiration.expiry?.expiry as number
        }
        monthlyLegsForRollOver.forEach(legIndex=>{
            legToExpiry[legIndex] = expiry
        })
    }

    if(nextMonthlyLegsForRollOver.length) {
        let expiry = monthlyOptionChain[1].expiration.expiry?.expiry as number
        if(
            (weeklyLegsExpiry[0] && weeklyLegsExpiry[0]===expiry) ||
            (weeklyLegsExpiry[1] && weeklyLegsExpiry[1]===expiry) ||
            (monthlyLegsForRollOver.length && legToExpiry[monthlyLegsForRollOver[0]] === expiry)
        ) {
            expiry = monthlyOptionChain[2].expiration.expiry?.expiry as number
        }
        nextMonthlyLegsForRollOver.forEach(legIndex=>{
            legToExpiry[legIndex] = expiry
        })
    }

    const expirySet = new Set<number>(Object.values(legToExpiry))
    const expiriesArr: number[] = []
    Array.from(expirySet).forEach((exp)=>{
        if((optionChain?.find(ex=>ex.expiration.expiry?.expiry===exp)?.trades.length??0)<25) {
            expiriesArr.push(exp)
        }
    })
    if(optionChainViewKey === OptionChainViewKey.LIVE && (expiriesArr.length || strategyTemplate.strategyTemplateLegs?.strategyTemplateLegs.some(temp=>temp.categoryCode===SecurityCategory_CategoryCode.FUTURES))) {
        const payload: GetOptionChainRequestV2 = {
            security: { code: security.code, exchange: security.exchange },
            expiries: expiriesArr,
            noOfStrikes: -1,
            requestedFields: [],
            timeBucket: {
                timeSeries: TimeBucket_TimeSeries.DAY,
                value: 1
            },
            sendFutures: true
        }
        const {optionsData, futuresData: futuresDataFromApi} = await fetchOptionChainV2(payload, store.dispatch, newOptionChain, 'both', false)
        newOptionChain = optionsData
        futuresData = futuresDataFromApi
    }
    monthlyOptionChain = newOptionChain.filter(o => !o.derived).filter(o => !o.expiration.weeklyExpiry)

    const expiryToOptionChain = newOptionChain.reduce((acc, item) => {
        acc[item.expiration.expiry?.expiry as number] = item
        return acc
      }, {} as Record<number, optionsTradesDataType>)

    for (let i = 0; i < (strategyTemplate.strategyTemplateLegs?.strategyTemplateLegs?.length ?? 0); i++) {
        const strategyLeg = strategyTemplate.strategyTemplateLegs?.strategyTemplateLegs[i] as StrategyTemplateLegProto
        const expiry = legToExpiry[i]
        const optionChainData = expiryToOptionChain[expiry]
        const optionExpiryIndex = newOptionChain.findIndex(o => o.expirySeconds === optionChainData.expirySeconds)

        let newOrder: OrdersDataType = {
            type: strategyLeg.transactionType === TransactionType.BUY ? 'buy' : 'sell',
            // security,
            expiry,
            quantity: strategyLeg.lots,
            uuid: uuid(),
            visibility: true
        } as OrdersDataType

        let tradeOrder: TradeOrder = {
            clientOrderId: uuid(),
            quantity: strategyLeg.lots,
            security: undefined, // temporarily set this to empty
            selected: true,
            transactionType: strategyLeg.transactionType
        }

        if (strategyLeg.categoryCode === SecurityCategory_CategoryCode.FUTURES) {
            const selectedFutureData = strategyLeg.expirySelection?.expirySelectionType === StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.MONTHLY ? futuresData[0] : futuresData[1]
            newOrder = {
                ...newOrder,
                data: selectedFutureData,
                security: selectedFutureData.security,
                contractType: 'FUTURE'
            }
            tradeOrder.security = selectedFutureData.security
        } else {
            let strike = optionChainData.atmStrike
            let atmIndex = optionChainData.trades.findIndex((t:any) => t.strikeCol.strike === strike)
            let data = optionChainData.trades[atmIndex]
            switch (strategyLeg.strikeCriteria?.strikeType) {
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.ATM_POINT: {
                    let currentStrikeIndex = atmIndex + strategyLeg.strikeCriteria.value
                    if (currentStrikeIndex < 0) currentStrikeIndex = 0
                    else if (currentStrikeIndex > optionChainData.trades.length - 1) currentStrikeIndex = optionChainData.trades.length - 1
                    data = optionChainData.trades[currentStrikeIndex]
                    strike = data.strikeCol.strike
                    break
                }
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.ATM_PERCENTAGE: {
                    const diff = (data.strikeCol.strike * strategyLeg.strikeCriteria.value) / 100
                    data = findGivenDiffStrike(data, diff, newOptionChain[optionExpiryIndex]) as optionTradeDataType
                    strike = data.strikeCol.strike
                    break
                }
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.STRADDLE_WIDTH: {
                    const SP = (data.callOption?.ltp ?? 0) + (data.putOption?.ltp ?? 0)
                    const diff = SP * strategyLeg.strikeCriteria.value
                    data = findGivenDiffStrike(data, diff, newOptionChain[optionExpiryIndex]) as optionTradeDataType
                    strike = data.strikeCol.strike
                    break
                }
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CLOSEST_PREMIUM: {
                    const premium = strategyLeg.strikeCriteria.value                    
                    const operator = strategyLeg.strikeCriteria?.parametersMap?.operator ?? EqualityOperator["~"]
                    data = findGivenPriceStrike(premium, newOptionChain[optionExpiryIndex], strategyLeg.optionType??OptionType.CE, operator)
                    strike = data.strikeCol.strike
                    break
                }
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CP_ON_STRADDLE_PREMIUM: {
                    const SP = (data.callOption?.ltp ?? 0) + (data.putOption?.ltp ?? 0)
                    const premium = SP * strategyLeg.strikeCriteria.value
                    const operator = strategyLeg.strikeCriteria?.parametersMap?.operator ?? EqualityOperator["~"]
                    data = findGivenPriceStrike(premium, newOptionChain[optionExpiryIndex], strategyLeg.optionType??OptionType.CE, operator)
                    strike = data.strikeCol.strike
                    break
                }
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.STANDARD_DEVIATION: {
                    const sdPercent = optionChainData.standardDeviation
                    const oneSdWidth = data.strikeCol.strike * (sdPercent / 100)
                    data = findGivenSdStrike(data, oneSdWidth, strategyLeg.strikeCriteria.value, newOptionChain, optionExpiryIndex) as optionTradeDataType
                    strike = data.strikeCol.strike
                    break
                }
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CLOSEST_DELTA: {
                    const cd = strategyLeg.strikeCriteria.value                    
                    const operator = strategyLeg.strikeCriteria?.parametersMap?.operator ?? EqualityOperator["~"]
                    data = findGivenDeltaStrike(cd, newOptionChain[optionExpiryIndex], strategyLeg.optionType??OptionType.CE, operator)
                    strike = data.strikeCol.strike
                    break
                }
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.SAME_AS_PREVIOUS_LEG: {
                    if(orders.length>0 && orders[orders.length-1].contractType === 'OPTION') {
                        data = findNearestStrikeData((orders[orders.length-1] as any).strike, newOptionChain[optionExpiryIndex]) as optionTradeDataType
                        strike = data.strikeCol.strike
                    }
                    break
                }
            }
            newOrder = {
                ...newOrder,
                data: data,
                strike: strike,
                contractType: 'OPTION',
                security: strategyLeg.optionType === OptionType.CE ? data.callOption?.security : data.putOption?.security,
                callput: strategyLeg.optionType === OptionType.CE ? 'call' : 'put',
            }
            const option = strategyLeg.optionType === OptionType.CE ? data.callOption : data.putOption;

            tradeOrder.security = (await getSecurityAsync(getSecurityKey(option?.security?.exchange ?? 0, option?.security?.code ?? ''))) ?? undefined
            tradeOrder.price = (tradeOrder.transactionType === TransactionType.BUY ? option.topAsk : option.topBid) || 0;
            if(optionChainViewKey===OptionChainViewKey.HISTORICAL) {
                newOrder.timeStamp = timeStamp
            }
        }
        orders.push(newOrder);
        tradeOrders.push(tradeOrder)
    }
    if (!dontSetOrdersInState) {
        if (optionChainViewKey === OptionChainViewKey.LIVE) {
            orderPrices = await getCurentOrderPricesFromData(orders, newOptionChain, getSecurityKey(security.exchange, security.code), [])
            setOrderDataFunction({ orders, resetPredefinedStrategy: false, resetOrderState: true, orderPrices, strategyName: strategyTemplate.name })
            commonActions.dispatchRightSideWidget(store.dispatch, WidgetListItems.TRADE)
            store.dispatch(updateTradeOrders(tradeOrders))
        } else {
            orderPrices = getCurentOrderLtpPricesFromData(orders, newOptionChain, getSecurityKey(security.exchange, security.code), orderPrices)
            setSimulatorOrderDataFunction({ orders, token: getSecurityKey(security.exchange, security.code), resetOrderState: false, orderPrices, strategyName: strategyTemplate.name})
        }
    } else {
        orderPrices = getCurentOrderLtpPricesFromData(orders, newOptionChain, getSecurityKey(security.exchange, security.code), orderPrices)
    }
    return {
        orders: orders,
        tradeOrders,
        orderPrices: orderPrices
    }
}


export const parseStrategyTemplateOrders = async (strategyTemplate: StrategyTemplateProto, security: Security, optionChain?: optionsTradesDataType[], orderType?: Broker_OrderType): Promise<TradeOrder[]> => {
    const tradeOrders: TradeOrder[] = [];
    let futuresData: futureTradeDataType[] = [];
    let updatedOptionChain = optionChain;

    if(!optionChain || optionChain.length === 0) {
        const payload: GetOptionChainRequestV2 = {
            security: { code: security.code, exchange: security.exchange },
            expiries: [],
            noOfStrikes: -1,
            requestedFields: [],
            timeBucket: {
                timeSeries: TimeBucket_TimeSeries.DAY,
                value: 1
            },
            sendFutures: true
        }
        const {optionsData, futuresData: futuresDataFromApi} = await fetchOptionChainV2(payload, store.dispatch, optionChain, 'both', false)
        updatedOptionChain = optionsData
        futuresData = futuresDataFromApi
    }
    let monthlyOptionChain = updatedOptionChain.filter(o => !o.derived).filter(o => !o.expiration.weeklyExpiry)
    const legToExpiry: Record<number, number> = {}
    const weeklyLegsExpiry: (number | undefined)[] = [undefined, undefined]
    const monthlyLegsForRollOver: number[] = []
    const nextMonthlyLegsForRollOver: number[] = []
    for (let i = 0; i < (strategyTemplate.strategyTemplateLegs?.strategyTemplateLegs?.length ?? 0); i++) {
        const strategyLeg = strategyTemplate.strategyTemplateLegs?.strategyTemplateLegs[i] as StrategyTemplateLegProto
        switch (strategyLeg.expirySelection?.expirySelectionType) {
            case StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.MONTHLY:
                legToExpiry[i] = monthlyOptionChain[0].expiration.expiry?.expiry as number
                if (strategyLeg.expirySelection.rolloverFlag) monthlyLegsForRollOver.push(i)
                break
            case StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.NEXT_MONTHLY:
                legToExpiry[i] = monthlyOptionChain[1].expiration.expiry?.expiry as number
                if (strategyLeg.expirySelection.rolloverFlag) nextMonthlyLegsForRollOver.push(i)
                break
            case StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.WEEKLY:
                legToExpiry[i] = updatedOptionChain[0].expiration.expiry?.expiry as number
                weeklyLegsExpiry[0] = legToExpiry[i]
                break
            case StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.NEXT_WEEKLY:
                legToExpiry[i] = updatedOptionChain[1].expiration.expiry?.expiry as number
                weeklyLegsExpiry[1] = legToExpiry[i]
                break
            case StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.MINIMUM_DAYS_TO_MONTHLY_EXPIRY:
                let index = 0
                for(let i=0; i<monthlyOptionChain.length; i++) {
                    if((monthlyOptionChain[i].expiration.expiry?.numberOfDaysToExpiry??0)>=(strategyLeg.expirySelection.minimumDaysToExpiry??0)) {
                        index = i
                        break
                    }
                }
                legToExpiry[i] = monthlyOptionChain[index].expiration.expiry?.expiry as number
                break
        }
    }

    if(monthlyLegsForRollOver.length) {
        let expiry = monthlyOptionChain[0].expiration.expiry?.expiry as number
        if(
            (weeklyLegsExpiry[0] && weeklyLegsExpiry[0]===expiry) ||
            (weeklyLegsExpiry[1] && weeklyLegsExpiry[1]===expiry)
        ) {
            expiry = monthlyOptionChain[1].expiration.expiry?.expiry as number
        }
        monthlyLegsForRollOver.forEach(legIndex=>{
            legToExpiry[legIndex] = expiry
        })
    }

    if(nextMonthlyLegsForRollOver.length) {
        let expiry = monthlyOptionChain[1].expiration.expiry?.expiry as number
        if(
            (weeklyLegsExpiry[0] && weeklyLegsExpiry[0]===expiry) ||
            (weeklyLegsExpiry[1] && weeklyLegsExpiry[1]===expiry) ||
            (monthlyLegsForRollOver.length && legToExpiry[monthlyLegsForRollOver[0]] === expiry)
        ) {
            expiry = monthlyOptionChain[2].expiration.expiry?.expiry as number
        }
        nextMonthlyLegsForRollOver.forEach(legIndex=>{
            legToExpiry[legIndex] = expiry
        })
    }

    const expirySet = new Set<number>(Object.values(legToExpiry))
    const expiriesArr: number[] = []
    Array.from(expirySet).forEach((exp)=>{
        if((updatedOptionChain?.find(ex=>ex.expiration.expiry?.expiry===exp)?.trades.length??0)<25) {
            expiriesArr.push(exp)
        }
    })
    if(expiriesArr.length || strategyTemplate.strategyTemplateLegs?.strategyTemplateLegs.some(temp=>temp.categoryCode===SecurityCategory_CategoryCode.FUTURES)) {
        const payload: GetOptionChainRequestV2 = {
            security: { code: security.code, exchange: security.exchange },
            expiries: expiriesArr,
            noOfStrikes: -1,
            requestedFields: [],
            timeBucket: {
                timeSeries: TimeBucket_TimeSeries.DAY,
                value: 1
            },
            sendFutures: true
        }
        const {optionsData, futuresData: futuresDataFromApi} = await fetchOptionChainV2(payload, store.dispatch, updatedOptionChain, 'both', false)
        updatedOptionChain = optionsData
        futuresData = futuresDataFromApi
    }
    monthlyOptionChain = updatedOptionChain.filter(o => !o.derived).filter(o => !o.expiration.weeklyExpiry)

    const expiryToOptionChain = updatedOptionChain.reduce((acc, item) => {
        acc[item.expiration.expiry?.expiry as number] = item
        return acc
      }, {} as Record<number, optionsTradesDataType>)

    for (let i = 0; i < (strategyTemplate.strategyTemplateLegs?.strategyTemplateLegs?.length ?? 0); i++) {
        const strategyLeg = strategyTemplate.strategyTemplateLegs?.strategyTemplateLegs[i] as StrategyTemplateLegProto
        const expiry = legToExpiry[i]
        const optionChainData = expiryToOptionChain[expiry]
        const optionExpiryIndex = updatedOptionChain.findIndex(o => o.expirySeconds === optionChainData.expirySeconds)

        let newOrder: OrdersDataType = {
            type: strategyLeg.transactionType === TransactionType.BUY ? 'buy' : 'sell',
            // security,
            expiry,
            quantity: strategyLeg.lots,
            uuid: uuid(),
            visibility: true
        } as OrdersDataType

        let tradeOrder: TradeOrder = {
            clientOrderId: uuid(),
            quantity: strategyLeg.lots,
            security: undefined, // temporarily set this to empty
            selected: true,
            transactionType: strategyLeg.transactionType
        }

        if (strategyLeg.categoryCode === SecurityCategory_CategoryCode.FUTURES) {
            const selectedFutureData = strategyLeg.expirySelection?.expirySelectionType === StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.MONTHLY ? futuresData[0] : futuresData[1]
            newOrder = {
                ...newOrder,
                data: selectedFutureData,
                security: selectedFutureData.security,
                contractType: 'FUTURE'
            }
            tradeOrder.security = selectedFutureData.security
        } else {
            let strike = optionChainData.atmStrike
            let atmIndex = optionChainData.trades.findIndex((t:any) => t.strikeCol.strike === strike)
            let data = optionChainData.trades[atmIndex]
            switch (strategyLeg.strikeCriteria?.strikeType) {
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.ATM_POINT: {
                    let currentStrikeIndex = atmIndex + strategyLeg.strikeCriteria.value
                    if (currentStrikeIndex < 0) currentStrikeIndex = 0
                    else if (currentStrikeIndex > optionChainData.trades.length - 1) currentStrikeIndex = optionChainData.trades.length - 1
                    data = optionChainData.trades[currentStrikeIndex]
                    strike = data.strikeCol.strike
                    break
                }
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.ATM_PERCENTAGE: {
                    const diff = (data.strikeCol.strike * strategyLeg.strikeCriteria.value) / 100
                    data = findGivenDiffStrike(data, diff, updatedOptionChain[optionExpiryIndex]) as optionTradeDataType
                    strike = data.strikeCol.strike
                    break
                }
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.STRADDLE_WIDTH: {
                    const SP = (data.callOption?.ltp ?? 0) + (data.putOption?.ltp ?? 0)
                    const diff = SP * strategyLeg.strikeCriteria.value
                    data = findGivenDiffStrike(data, diff, updatedOptionChain[optionExpiryIndex]) as optionTradeDataType
                    strike = data.strikeCol.strike
                    break
                }
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CLOSEST_PREMIUM: {
                    const premium = strategyLeg.strikeCriteria.value                    
                    const operator = strategyLeg.strikeCriteria?.parametersMap?.operator ?? EqualityOperator["~"]
                    data = findGivenPriceStrike(premium, updatedOptionChain[optionExpiryIndex], strategyLeg.optionType??OptionType.CE, operator)
                    strike = data.strikeCol.strike
                    break
                }
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CP_ON_STRADDLE_PREMIUM: {
                    const SP = (data.callOption?.ltp ?? 0) + (data.putOption?.ltp ?? 0)
                    const premium = SP * strategyLeg.strikeCriteria.value
                    const operator = strategyLeg.strikeCriteria?.parametersMap?.operator ?? EqualityOperator["~"]
                    data = findGivenPriceStrike(premium, updatedOptionChain[optionExpiryIndex], strategyLeg.optionType??OptionType.CE, operator)
                    strike = data.strikeCol.strike
                    break
                }
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.STANDARD_DEVIATION: {
                    const sdPercent = optionChainData.standardDeviation
                    const oneSdWidth = data.strikeCol.strike * (sdPercent / 100)
                    data = findGivenSdStrike(data, oneSdWidth, strategyLeg.strikeCriteria.value, updatedOptionChain, optionExpiryIndex) as optionTradeDataType
                    strike = data.strikeCol.strike
                    break
                }
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CLOSEST_DELTA: {
                    const cd = strategyLeg.strikeCriteria.value                    
                    const operator = strategyLeg.strikeCriteria?.parametersMap?.operator ?? EqualityOperator["~"]
                    data = findGivenDeltaStrike(cd, updatedOptionChain[optionExpiryIndex], strategyLeg.optionType??OptionType.CE, operator)
                    strike = data.strikeCol.strike
                    break
                }
                case StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.SAME_AS_PREVIOUS_LEG: {
                    if(tradeOrders.length>0 && tradeOrders[tradeOrders.length-1].security?.categoryCode === SecurityCategory_CategoryCode.OPTIONS) {
                        data = findNearestStrikeData(tradeOrders[tradeOrders.length-1].security.strikePrice, updatedOptionChain[optionExpiryIndex]) as optionTradeDataType
                        strike = data.strikeCol.strike
                    }
                    break
                }
            }
            newOrder = {
                ...newOrder,
                data: data,
                strike: strike,
                contractType: 'OPTION',
                security: strategyLeg.optionType === OptionType.CE ? data.callOption?.security : data.putOption?.security,
                callput: strategyLeg.optionType === OptionType.CE ? 'call' : 'put',
            }
            const option = strategyLeg.optionType === OptionType.CE ? data.callOption : data.putOption;

            tradeOrder.security = (await getSecurityAsync(getSecurityKey(option?.security?.exchange ?? 0, option?.security?.code ?? ''))) ?? undefined
            tradeOrder.price = orderType === Broker_OrderType.MARKET_ORDER ? 0 : (tradeOrder.transactionType === TransactionType.BUY ? option.topAsk : option.topBid) || 0;
            tradeOrder.quantity *= (tradeOrder.security.lotSize??1)
        }
        tradeOrders.push(tradeOrder)
    }
    return tradeOrders;
}